import { Address } from "@ton/core";

const REFERRAL_STORAGE_KEY = "lotteryReferralAddress";

type TelegramWindow = Window & {
  Telegram?: {
    WebApp?: {
      initDataUnsafe?: {
        start_param?: string;
      };
    };
  };
};

function safeParseAddress(value: string | null | undefined): string | null {
  if (!value) return null;

  const normalized = value.trim();
  if (!normalized) return null;

  try {
    Address.parse(normalized);
    return normalized;
  } catch {
    return null;
  }
}

function extractAddressCandidate(value: string | null | undefined): string | null {
  if (!value) return null;

  const direct = safeParseAddress(value);
  if (direct) return direct;

  let decoded = value;

  try {
    decoded = decodeURIComponent(value);
  } catch {
    decoded = value;
  }

  const decodedDirect = safeParseAddress(decoded);
  if (decodedDirect) return decodedDirect;

  const fromRefPattern = decoded.match(/(?:^|[?&])ref=([^&]+)/i)?.[1] ?? null;
  const fromRef = safeParseAddress(fromRefPattern);
  if (fromRef) return fromRef;

  const query = new URLSearchParams(decoded);
  const nestedRef = safeParseAddress(query.get("ref"));
  if (nestedRef) return nestedRef;

  return null;
}

export function persistReferralAddressFromEntry(): string | null {
  if (typeof window === "undefined") return null;

  const searchParams = new URLSearchParams(window.location.search);
  const telegramStartParam = (window as TelegramWindow).Telegram?.WebApp?.initDataUnsafe?.start_param;

  const referralAddress =
    extractAddressCandidate(searchParams.get("ref")) ??
    extractAddressCandidate(searchParams.get("startapp")) ??
    extractAddressCandidate(searchParams.get("start")) ??
    extractAddressCandidate(searchParams.get("tgWebAppStartParam")) ??
    extractAddressCandidate(telegramStartParam);

  if (!referralAddress) return null;

  try {
    window.localStorage.setItem(REFERRAL_STORAGE_KEY, referralAddress);
  } catch {
    return null;
  }

  return referralAddress;
}

export function getStoredReferralAddress(): string | null {
  if (typeof window === "undefined") return null;

  try {
    return safeParseAddress(window.localStorage.getItem(REFERRAL_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function clearStoredReferralAddress() {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    // Ignore storage errors.
  }
}

export function areSameTonAddress(left: string, right: string): boolean {
  try {
    return Address.parse(left).equals(Address.parse(right));
  } catch {
    return false;
  }
}